// "Your life in care" — the Overview's whole-life view, worked out in the
// browser from the person's organisations (GET /organisations/enriched,
// placements and people opened by the vault interceptor) and their date of
// birth. Pure functions, tested in lifeInCare.test.js; rendered by
// components/dashboard/LifeInCare.jsx and PlacesInYourLife.jsx.
import { monthsBetween, spanWords, ageAt, yearSpan, samePerson } from './orgProfile';
import { yearGroupsToDates } from './schoolYears';

export const PLACING_TYPES = new Set(['council', 'fostering_agency']);
const HEALTH_TYPES = new Set(['doctor', 'hospital', 'mental_health']);
const CARER_LED = new Set(['foster_placement', 'kinship', 'supported_lodgings']);
const HOME_STAFF_ROLES = new Set(['key_worker', 'residential_worker']);

const thisMonth = (today = new Date()) => `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
const ym = (s) => (/^\d{4}-\d{2}/.test(String(s || '')) ? String(s).slice(0, 7) : null);

export const homeName = (p) => p?.name || (p?.carer_names?.length ? p.carer_names.join(' and ') : 'A home');

/** An organisation's own dates, a school's from its year groups when it has no dates. */
export const orgDates = (org, dob) => {
  if (ym(org.start_date) || org.type !== 'school' || !dob) return { start: ym(org.start_date), end: ym(org.end_date) };
  const d = yearGroupsToDates(dob, org.year_group_start, org.year_group_end);
  return { start: ym(d.start_date), end: ym(d.end_date) };
};

/**
 * Every home, from the councils and agencies that placed the person (and
 * any holding organisation). A children's home that is its own organisation
 * is counted once, by the placement that links to it.
 */
export const allHomes = (orgs = []) => orgs
  .filter((o) => PLACING_TYPES.has(o.type))
  .flatMap((o) => (o.placements || []).map((p) => ({ placement: p, org: o })))
  .sort((a, b) => (ym(a.placement.start_date) || '9999').localeCompare(ym(b.placement.start_date) || '9999'));

/** When care began and ended, from the placing organisations and their homes. */
export const careSpan = (orgs = [], today) => {
  const starts = [];
  const ends = [];
  let ongoing = false;
  for (const o of orgs.filter((x) => PLACING_TYPES.has(x.type))) {
    const items = [{ start_date: o.start_date, end_date: o.end_date }, ...(o.placements || [])];
    for (const it of items) {
      if (!ym(it.start_date)) continue;
      starts.push(ym(it.start_date));
      if (ym(it.end_date)) ends.push(ym(it.end_date));
      else ongoing = true;
    }
  }
  if (!starts.length) return null;
  starts.sort();
  ends.sort();
  const end = ongoing ? null : ends[ends.length - 1] || null;
  return { start: starts[0], end, ongoing, span: spanWords(starts[0], end, today) };
};

/** "10 years and 10 months, from when you were 7 until you were 18." */
export const careSentence = (span, dob) => {
  if (!span?.span) return null;
  const words = span.span.replace(', ', ' and ');
  const from = ageAt(dob, span.start);
  const to = span.end ? ageAt(dob, span.end) : null;
  if (from !== null && to !== null && to > from) return `${words}, from when you were ${from} until you were ${to}.`;
  if (from !== null && span.ongoing) return `${words} so far, since you were ${from}.`;
  return `${words}.`;
};

const item = (id, label, start, end, href) => ({ id, label, start, end, href });

/**
 * The lines of the life strip — who was responsible, where you lived,
 * school, health — each a list of dated items, plus anything that has no
 * dates yet (shown as a gap to fill). Health only when there's something
 * on it.
 */
export const lifeLanes = (orgs = [], dob) => {
  const lanes = [];
  const lane = (id, label, list, always = true) => {
    const dated = list.filter((x) => x.start).sort((a, b) => a.start.localeCompare(b.start));
    const undated = list.filter((x) => !x.start);
    if (always || list.length) lanes.push({ id, label, items: dated, undated });
  };
  const placing = orgs.filter((o) => PLACING_TYPES.has(o.type) && !o.unresolved);
  lane('responsible', 'Responsible', placing.map((o) => item(o.id, o.name, ym(o.start_date), ym(o.end_date), `/organisations/${o.id}`)));
  lane('homes', 'Where you lived', allHomes(orgs).map(({ placement: p, org: o }) => item(
    `${o.id}:${p.id}`, homeName(p), ym(p.start_date), ym(p.end_date),
    p.linked_org_id ? `/organisations/${p.linked_org_id}` : p.id ? `/organisations/${o.id}/placements/${p.id}` : `/organisations/${o.id}`,
  )));
  lane('school', 'School', orgs.filter((o) => o.type === 'school').map((o) => {
    const d = orgDates(o, dob);
    return item(o.id, o.name, d.start, d.end, `/organisations/${o.id}`);
  }));
  lane('health', 'Health', orgs.filter((o) => HEALTH_TYPES.has(o.type)).map((o) => item(o.id, o.name, ym(o.start_date), ym(o.end_date), `/organisations/${o.id}`)), false);
  return lanes;
};

/**
 * Lay the lanes out on one shared axis: each lane becomes blocks (sized by
 * months, `null` end = now) and blank gaps, from the earliest start to the
 * latest end across every lane. Overlapping items in one lane are clipped
 * to start where the previous one ended so a line never doubles back.
 */
export const layoutLanes = (lanes, today) => {
  const now = thisMonth(today);
  const all = lanes.flatMap((l) => l.items);
  if (!all.length) return null;
  const start = all.map((x) => x.start).sort()[0];
  const end = all.map((x) => x.end || now).sort().pop();
  const total = Math.max(1, monthsBetween(start, end) || 1);
  return {
    start, end, total,
    lanes: lanes.map((l) => {
      const blocks = [];
      let cursor = start;
      for (const it of l.items) {
        const s = it.start < cursor ? cursor : it.start;
        const e = it.end || now;
        if (e < s) continue;
        const gap = monthsBetween(cursor, s) || 0;
        if (gap > 0) blocks.push({ gap: true, months: gap });
        blocks.push({ ...it, months: Math.max(1, monthsBetween(s, e) || 0) });
        cursor = e;
      }
      const tail = monthsBetween(cursor, end) || 0;
      if (tail > 0) blocks.push({ gap: true, months: tail });
      return { ...l, blocks };
    }),
  };
};

/** Axis labels: year, with the age then when the date of birth is known. */
export const axisTicks = (start, end, dob, max = 6) => {
  const a = +String(start).slice(0, 4);
  const b = +String(end).slice(0, 4);
  if (!a || !b) return [];
  const years = [];
  const step = Math.max(1, Math.ceil((b - a) / (max - 1)));
  for (let y = a; y < b; y += step) years.push(y);
  years.push(b);
  return [...new Set(years)].map((y) => {
    const age = ageAt(dob, `${y}-${y === a ? String(start).slice(5, 7) || '01' : '06'}`);
    return { year: y, label: age !== null ? `${y} · ${age}` : String(y) };
  });
};

const uniqueNames = (names) => names.reduce((out, n) => {
  if (n && !out.some((x) => samePerson(x, n))) out.push(n);
  return out;
}, []);

/**
 * The whole-life numbers: homes and moves, schools, social workers, people
 * who cared for you. A tile with nothing to count is left out.
 */
export const lifeTiles = (orgs = [], { dob, respitePool = [] } = {}) => {
  const tiles = [];
  const homes = allHomes(orgs);
  if (homes.length) {
    const longest = homes
      .map(({ placement: p }) => ({ p, n: monthsBetween(p.start_date, p.end_date) }))
      .filter((x) => x.n !== null && x.n >= 3)
      .sort((x, y) => y.n - x.n)[0];
    tiles.push({
      id: 'homes', label: homes.length === 1 ? 'Home' : 'Homes', value: homes.length, to: '/my-life',
      hint: [homes.length > 1 ? `${homes.length - 1} move${homes.length === 2 ? '' : 's'}` : null,
        longest && homes.length > 1 ? `the longest was ${spanWords(longest.p.start_date, longest.p.end_date)} ${CARER_LED.has(longest.p.type) ? 'with' : 'at'} ${homeName(longest.p)}` : null]
        .filter(Boolean).join(' · '),
    });
  }
  const schools = orgs.filter((o) => o.type === 'school');
  if (schools.length) {
    // The school that saw you through the most moves, if any did.
    const steadiest = schools.map((s) => {
      const d = orgDates(s, dob);
      const during = d.start ? homes.filter(({ placement: p }) => ym(p.start_date) && ym(p.start_date) > d.start && ym(p.start_date) <= (d.end || thisMonth())).length : 0;
      return { s, during };
    }).sort((x, y) => y.during - x.during)[0];
    tiles.push({
      id: 'schools', label: schools.length === 1 ? 'School' : 'Schools', value: schools.length, to: '/organisations',
      hint: steadiest?.during ? `you stayed at ${steadiest.s.name} through ${steadiest.during} move${steadiest.during === 1 ? '' : 's'}` : null,
    });
  }
  const people = orgs.flatMap((o) => (o.social_workers || []).map((p) => ({ ...p, orgType: o.type })));
  const sws = uniqueNames(people.filter((p) => p.role === 'social_worker').map((p) => p.name));
  const pas = uniqueNames(people.filter((p) => p.role === 'leaving_care_pa').map((p) => p.name));
  if (sws.length) {
    tiles.push({ id: 'social_workers', label: sws.length === 1 ? 'Social worker' : 'Social workers', value: sws.length, to: '/organisations', hint: pas.length ? `and ${pas.length === 1 ? 'a personal adviser' : `${pas.length} personal advisers`}` : null });
  } else if (pas.length) {
    tiles.push({ id: 'pas', label: pas.length === 1 ? 'Personal adviser' : 'Personal advisers', value: pas.length, to: '/organisations', hint: null });
  }
  const respiteIds = new Set(homes.flatMap(({ placement: p }) => (p.respite || []).map((e) => e.carer_id)));
  const carers = uniqueNames([
    ...homes.flatMap(({ placement: p }) => (CARER_LED.has(p.type) ? p.carer_names || [] : [])),
    ...respitePool.filter((c) => respiteIds.has(c.id)).map((c) => c.name),
    ...people.filter((p) => p.role === 'foster_carer' || HOME_STAFF_ROLES.has(p.role)).map((p) => p.name),
  ]);
  if (carers.length) {
    const kinds = [
      homes.some(({ placement: p }) => p.type === 'foster_placement' && p.carer_names?.length) ? 'foster carers' : null,
      homes.some(({ placement: p }) => p.type === 'kinship' && p.carer_names?.length) ? 'family' : null,
      respiteIds.size ? 'short breaks' : null,
      people.some((p) => HOME_STAFF_ROLES.has(p.role)) ? 'key workers' : null,
    ].filter(Boolean);
    tiles.push({ id: 'carers', label: 'People who cared for you', value: carers.length, to: '/my-life', hint: kinds.length > 1 ? `${kinds.slice(0, -1).join(', ')} and ${kinds[kinds.length - 1]}` : kinds[0] || null });
  }
  return tiles;
};

const PLACE_GROUPS = [
  { id: 'responsible', title: 'Who was responsible', types: new Set(['council', 'fostering_agency', 'adoption', 'childrens_home']) },
  { id: 'school', title: 'School', types: new Set(['school']) },
  { id: 'health', title: 'Health', types: HEALTH_TYPES },
  { id: 'other', title: 'Others', types: null },
];

const YEAR_LABEL = (v) => (v ? String(v).replace('year_', 'Year ').replace('reception', 'Reception') : null);

/** "2008 – 2019 · age 7 to 18", "Year 7 to Year 11 · age 11 to 16". */
export const placeWhen = (org, dob) => {
  const d = orgDates(org, dob);
  const years = org.type === 'school' && org.year_group_start
    ? [YEAR_LABEL(org.year_group_start), org.year_group_end && org.year_group_end !== org.year_group_start ? YEAR_LABEL(org.year_group_end) : null].filter(Boolean).join(' to ')
    : yearSpan(d.start, d.end);
  const a = ageAt(dob, d.start);
  const b = d.end ? ageAt(dob, d.end) : null;
  const ages = a !== null && b !== null && b > a ? `age ${a} to ${b}` : a !== null && !d.end ? `from age ${a}` : null;
  return [years, ages].filter(Boolean).join(' · ') || null;
};

/** The organisations, grouped by the part of life they were, each group in time order. */
export const placesInYourLife = (orgs = [], dob) => {
  const used = new Set();
  return PLACE_GROUPS.map((g) => {
    const list = orgs.filter((o) => !used.has(o.id) && (g.types ? g.types.has(o.type) : true));
    list.forEach((o) => used.add(o.id));
    return {
      ...g,
      orgs: list
        .map((o) => ({ org: o, start: orgDates(o, dob).start, when: placeWhen(o, dob) }))
        .sort((a, b) => (a.start || '9999').localeCompare(b.start || '9999') || a.org.name.localeCompare(b.org.name)),
    };
  }).filter((g) => g.orgs.length);
};

/** The first council or agency with no dates — the Overview offers to add them. */
export const undatedPlacingOrg = (orgs = []) => orgs.find((o) => PLACING_TYPES.has(o.type) && !o.unresolved && !ym(o.start_date)) || null;
